import React, { useEffect, useMemo, useState } from 'react';
import './Score.css';
import { useSocket } from '../context/SocketContext';
import ProfileImage from '../components/ProfileImage';
import BrandFooter from '../components/BrandFooter';
import { apiUrl } from '../config';

const HERO_SRC = `${process.env.PUBLIC_URL}/assets/gameinyang_logo2.png`;
const STAR_SRC = `${process.env.PUBLIC_URL}/assets/yellow_star.png`;

// "2026.04.06 PM.14:50" — always rendered in KST regardless of device TZ.
function formatKST(date) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Seoul',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(date);
    const p = {};
    parts.forEach((x) => { p[x.type] = x.value; });
    let h = parseInt(p.hour, 10);
    if (h === 24) h = 0; // Intl can emit 24 for midnight
    const ampm = h < 12 ? 'AM' : 'PM';
    return `${p.year}.${p.month}.${p.day} ${ampm}.${String(h).padStart(2, '0')}:${p.minute}`;
  } catch {
    return '';
  }
}

/**
 * CONTENTS_ENDED — results screen (shown when /api/EndContents is called).
 * Final score + rank come from GET /api/finalScore/:userId (authoritative,
 * preserved by the backend through CONTENTS_ENDED); the live score/rank in
 * SocketContext are the fallback.
 */
export default function Score() {
  const { userInfo, score, rank } = useSocket();

  const [heroFailed, setHeroFailed] = useState(false);
  const [starFailed, setStarFailed] = useState(false);
  const [final, setFinal] = useState(null);

  // Captured once when the result screen appears.
  const timestamp = useMemo(() => formatKST(new Date()), []);

  useEffect(() => {
    const uid = userInfo.userId;
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          apiUrl(`/api/finalScore/${encodeURIComponent(uid)}`),
          { cache: 'no-store' }
        );
        const json = await res.json();
        if (!cancelled && json?.ok) setFinal(json);
      } catch {
        /* fall back to the live values from SocketContext */
      }
    })();
    return () => { cancelled = true; };
  }, [userInfo.userId]);

  const total = final?.total ?? score.total ?? 0;
  const finalRank = final?.rank ?? rank;

  const rankText = finalRank != null ? `${finalRank}등` : '-';
  // Star grows with the rank's digit count so longer ranks don't bleed out.
  const digits = finalRank != null ? String(finalRank).length : 1;
  const starSize = 96 + Math.max(0, digits - 1) * 28;     // 96 / 124 / 152
  const rankFontSize = 22 + Math.max(0, digits - 1) * 2;  // 22 / 24 / 26

  return (
    <div className="score-screen">
      <div className="score-inner">
        <div className="score-hero">
          {heroFailed ? (
            <div className="score-hero-fallback">게임이냥</div>
          ) : (
            <img
              src={HERO_SRC}
              alt="게임이냥"
              className="score-hero-img"
              onError={() => setHeroFailed(true)}
              draggable={false}
            />
          )}
        </div>

        <ProfileImage
          index={userInfo.profileIndex}
          src={userInfo.profileImage}
          className="score-profile-img"
          alt={userInfo.nickname}
        />
        <div className="score-nickname">{userInfo.nickname}</div>

        <div className="score-label">SCORE</div>
        <div className="score-value">{total}</div>

        {/* rank star */}
        <div className="score-star" style={{ width: starSize, height: starSize }}>
          {starFailed ? (
            <div className="score-star-fallback" />
          ) : (
            <img
              src={STAR_SRC}
              alt=""
              aria-hidden="true"
              className="score-star-img"
              onError={() => setStarFailed(true)}
              draggable={false}
            />
          )}
          <span
            className="score-star-text"
            style={{
              fontSize: rankFontSize,
              // nudge down into the star's visual body (top is a narrow point)
              transform: `translateY(${Math.round(starSize * 0.07)}px)`,
            }}
          >
            {rankText}
          </span>
        </div>

        <div className="score-timestamp">{timestamp}</div>

        <div className="score-guide">
          화면을 캡쳐하여 저장하세요
        </div>
      </div>
      <BrandFooter />
    </div>
  );
}
